import { isAudioMedia } from '@lib/utils/fileExtensions';
import { showToast } from '@lib/utils/toastmaster';
import { LibraryStore } from '@stores/Library/Library';
import { useLibraryFolder } from '@stores/Library/LibraryFolder';
import { useLibraryManager } from '@stores/LibraryManager/LibraryManager';
import { useCallback, useMemo, useState } from 'react';

/**
 * Shared state + logic for the "edit an existing library" flow (name, thumbnail,
 * and custom word hints / glossary). Both the desktop `LibraryRenameModal` and the
 * mobile `MobileEditLibraryModal` are the same operation with different shells —
 * this hook owns everything except the presentation, so the two can't drift.
 *
 * The create flow (`createLibraryFlow.tsx`) is intentionally NOT unified here: it's
 * a different operation (create, no seed / no change-detection) that only shares the
 * `useWordHints ? words : []` idiom.
 */
export function useLibraryEditForm(libraryId: string, currentName: string) {
  const libraryManager = useLibraryManager();
  const folder = useLibraryFolder();
  const videoFiles = useMemo(() => folder.files.filter((f) => !isAudioMedia(f)), [folder.files]);

  const library = libraryManager.libraries.find((l) => l.libraryId === libraryId) as
    | LibraryStore
    | undefined;

  const [newName, setNewName] = useState('');
  const [newThumbnailMediaId, setNewThumbnailMediaId] = useState('');
  const [originalThumbnailMediaId, setOriginalThumbnailMediaId] = useState('');
  const [glossary, setGlossary] = useState<string[]>([]);
  // Whether this library uses custom word hints. Off → the glossary is cleared on save.
  const [useWordHints, setUseWordHints] = useState(false);
  const [isSavingChanges, setIsSavingChanges] = useState(false);

  const changes = useMemo(() => {
    // Compare the trimmed name so change-detection agrees with what submit() actually
    // saves (`newName.trim() || currentName`) — otherwise whitespace-only input reads as
    // a change but saves nothing, and the success toast falsely reports a rename.
    const trimmedName = newName.trim();
    const nameChanged = trimmedName !== currentName && trimmedName !== '';
    const thumbnailChanged = newThumbnailMediaId !== originalThumbnailMediaId;
    const currentGlossary = library?.data?.settings?.v1?.glossary || [];
    // Switch off → no word hints, i.e. an empty glossary regardless of the list.
    const effectiveGlossary = useWordHints ? glossary : [];
    const glossaryChanged =
      JSON.stringify([...effectiveGlossary].sort()) !== JSON.stringify([...currentGlossary].sort());
    return {
      nameChanged,
      thumbnailChanged,
      glossaryChanged,
      hasChanges: nameChanged || thumbnailChanged || glossaryChanged,
    };
  }, [
    newName,
    currentName,
    newThumbnailMediaId,
    originalThumbnailMediaId,
    glossary,
    useWordHints,
    library,
  ]);

  /** Populate the form from the current library. Call when the modal opens. */
  const seed = useCallback(() => {
    const initialThumbnailId =
      library?.thumbnailMediaId || videoFiles[0]?._id || folder.files[0]?._id || '';
    setNewName(currentName);
    setNewThumbnailMediaId(initialThumbnailId);
    setOriginalThumbnailMediaId(initialThumbnailId);
    const currentGlossary = library?.data?.settings?.v1?.glossary || [];
    setGlossary([...currentGlossary]);
    setUseWordHints(currentGlossary.length > 0);
  }, [library, videoFiles, folder.files, currentName]);

  /** Clear the form. Call when the modal closes. */
  const reset = useCallback(() => {
    setNewName('');
    setNewThumbnailMediaId('');
    setOriginalThumbnailMediaId('');
    setGlossary([]);
    setUseWordHints(false);
  }, []);

  /**
   * Persist the changes and fire the success/error toast. Returns `true` on success
   * so the caller can close its shell. Never overwrites the name with an empty string
   * — an empty field means "keep the current name" (mirrors the create flow's DEFAULT
   * fallback), so clearing the name and changing only the thumbnail / word hints can't
   * blank the library name.
   */
  const submit = useCallback(async () => {
    setIsSavingChanges(true);
    const nameToSave = newName.trim() || currentName;
    try {
      // Only send the glossary when it actually changed. `undefined` tells modifyLibrary to
      // leave settings untouched, so a name/thumbnail-only edit can never clear existing word
      // hints — even if the form seeded from a library whose settings hadn't hydrated yet.
      const glossaryToSave = changes.glossaryChanged ? (useWordHints ? glossary : []) : undefined;
      await libraryManager.modifyLibrary(
        libraryId,
        nameToSave,
        newThumbnailMediaId,
        glossaryToSave,
      );
      if (changes.hasChanges) {
        const oldMediaTitle =
          folder.files.find((f) => f._id === originalThumbnailMediaId)?.metadata?.name || '';
        const newMediaTitle =
          folder.files.find((f) => f._id === newThumbnailMediaId)?.metadata?.name || '';
        showToast('library-modify-success', {
          name: nameToSave,
          oldName: currentName,
          thumbnailMediaId: newThumbnailMediaId,
          nameChanged: changes.nameChanged,
          thumbnailChanged: changes.thumbnailChanged,
          oldMediaTitle,
          newMediaTitle,
        });
      }
      return true;
    } catch (error) {
      console.error(error);
      showToast('library-modify-error', {
        name: nameToSave,
        thumbnailMediaId: newThumbnailMediaId,
      });
      return false;
    } finally {
      setIsSavingChanges(false);
    }
  }, [
    libraryManager,
    libraryId,
    newName,
    currentName,
    newThumbnailMediaId,
    originalThumbnailMediaId,
    glossary,
    useWordHints,
    changes,
    folder.files,
  ]);

  return {
    library,
    videoFiles,
    newName,
    setNewName,
    newThumbnailMediaId,
    setNewThumbnailMediaId,
    glossary,
    setGlossary,
    useWordHints,
    setUseWordHints,
    isSavingChanges,
    changes,
    seed,
    reset,
    submit,
  };
}
